import React, { useState } from "react";
import {
  Typography,
  Drawer,
  Row,
  Col,
  Select,
  Button,
  message,
  DatePicker,
  Divider,
  Radio,
} from "antd";
import { SendOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import {
  SEND_NOTIFICATION,
  UPDATE_NOTIFICATION,
  GET_SUBZONES,
  GET_ZONES,
} from "./requests";
import moment from "moment-timezone";
import { Container } from "../../../elements";
import { notificationTypes } from "../../../../../types";

const { Title, Text } = Typography;
const { Option } = Select;

type SendNotificationProps = {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  selectedNotification: typeof notificationTypes.notification | null;
  setSelectedNotification: (value: typeof notificationTypes.notification | null) => void;
  refetch: () => Promise<any>;
};

const SendNotification: React.FC<SendNotificationProps> = ({
  showModal,
  setShowModal,
  selectedNotification,
  setSelectedNotification,
  refetch,
}) => {
  const [isNow, setIsNow] = useState<boolean | undefined>(undefined);
  const [scheduledDate, setScheduledDate] = useState<moment.Moment | null>(null);
  const [scheduledDateSelected, setScheduledDateSelected] = useState<moment.Moment | null>(null);
  const [notificationType, setNotificationType] = useState<string | null>(null);
  const [selectedZones, setSelectedZones] = useState<string[] | []>([]);
  const [selectedSubZones, setSelectedSubZones] = useState<string[] | []>([]);

  const {
    data: allSubZones, loading: loadingSubZone,
  } = useQuery(GET_SUBZONES, {
    variables: {
      params: { page: 1,
        pageSize: 200 },
    },
    skip: notificationType !== "subZone",
  });

  const {
    data: allZones, loading: loadingZone,
  } = useQuery(GET_ZONES, {
    variables: {
      params: { page: 1,
        pageSize: 200 },
    },
    skip: notificationType !== "zone",
  });

  const subZones = allSubZones?.subZones?.results ?? [];
  const zones = allZones?.zones?.results ?? [];

  const onClose = () => {
    setShowModal(false);
    setSelectedNotification(null);
    setScheduledDate(null);
    setScheduledDateSelected(null);
    setNotificationType(null);
    setSelectedZones([]);
    setSelectedSubZones([]);
    setIsNow(undefined)
  };

  const handleRadioChange = (value: boolean) => {
    setIsNow(value);
    if (value) {
      setScheduledDate(moment());
    }
  };

  const handleChangeDate = (date: moment.Moment | null) => {
    setScheduledDateSelected(date);
  };

  const [sendNotificationMutation] = useMutation(SEND_NOTIFICATION);
  const [updateNotificationMutation] = useMutation(UPDATE_NOTIFICATION);

  const sendNotification = async () => {
    if (!selectedNotification) return;

    try {
      const payloadSend = {
        variables: {
          id: selectedNotification.id,
          ...(notificationType === "zone" && { zones: selectedZones }),
          ...(notificationType === "subZone" && { subZones: selectedSubZones }),
        },
      };
      await sendNotificationMutation(payloadSend);

      const payloadUpdate = {
        variables: {
          id: selectedNotification.id,
          notification: {
            scheduledDate: (isNow ? scheduledDate : scheduledDateSelected)?.toISOString(),
            ...(notificationType === "zone" && { zones: selectedZones }),
            ...(notificationType === "subZone" && { subZones: selectedSubZones }),
          },
        },
      };
      await updateNotificationMutation(payloadUpdate);

      message.success("La notificación se envió con éxito!");
      await refetch();
    } catch (err) {
      console.error(err);
      message.error("Error al enviar");
    } finally {
      onClose();
    }
  };

  return (
    <Container>
      <Drawer
        open={showModal}
        onClose={onClose}
        title={<Text style={{ fontSize: "19px" }}>{selectedNotification?.title}</Text>}
        width={"45%"}
      >
        <Row>
          <Col>
            <Title level={5}>
              ¿A quien se enviará la notificación?
            </Title>
            <Radio.Group
              value={notificationType}
              onChange={(e) => setNotificationType(e.target.value)}
            >
              <Radio.Button value={null}>Todos</Radio.Button>
              <Radio.Button value={"zone"}>Zona</Radio.Button>
              <Radio.Button value={"subZone"}>SubZona</Radio.Button>
            </Radio.Group>
          </Col>
        </Row>
        {notificationType === "zone" && (
          <Select
            size="large"
            loading={loadingZone}
            mode="multiple"
            value={selectedZones}
            style={{ width: "85%",
              marginTop:10 }}
            placeholder="Seleccionar zonas"
            onChange={(value) => setSelectedZones(value)}
            showSearch
            filterOption={(input, option) => {
              const groupName = typeof option?.children === 'string' ? option.children : '';
              return groupName.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {zones.map(({ id, name }) => (
              <Select.Option key={id} value={id}>
                {name}
              </Select.Option>
            ))}
          </Select>
        )}
        {notificationType === "subZone" && (
          <Select
            size="large"
            loading={loadingSubZone}
            mode="multiple"
            value={selectedSubZones}
            style={{ width: "85%",
              marginTop:10 }}
            placeholder="Seleccionar subZonas"
            onChange={(value) => setSelectedSubZones(value)}
            showSearch
            filterOption={(input, option) => {
              const groupName = typeof option?.children === 'string' ? option.children : '';
              return groupName.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {subZones.map(({ id, name }) => (
              <Option key={id} value={id}>
                {name}
              </Option>
            ))}
          </Select>
        )}
        <Row style={{ marginTop: 20 }}>
          <Col>
            <Title level={5}>
              ¿La notificación se enviará ahora?
            </Title>
            <Radio.Group
              value={isNow}
              onChange={(e) => handleRadioChange(e.target.value)}
            >
              <Radio.Button value={true}>Sí</Radio.Button>
              <Radio.Button value={false}>No</Radio.Button>
            </Radio.Group>
          </Col>
        </Row>
        {isNow && (
          <Text strong style={{ fontSize: 14 }}>
            Fecha de Envío:{" "}
            <Text style={{ fontSize: 14 }}>
              {moment().format("D MMMM YYYY, HH:mm")}
            </Text>
          </Text>
        )}
        {(!isNow && isNow !== undefined) && (
          <DatePicker
            style={{ marginTop: 20 }}
            size="large"
            format="YYYY-MM-DD HH:mm"
            showTime={{ defaultValue: moment("00:00", "HH:mm") as any }}
            onChange={handleChangeDate}
            disabledDate={(current) =>
              current && current < moment().startOf("day")}
            value={scheduledDateSelected}
            placeholder="Selecciona la fecha"
          />
        )}

        <Divider />

        <Button
          size="large"
          type="primary"
          icon={<SendOutlined />}
          onClick={sendNotification}
          style={{ marginTop: 20 }}
          disabled={
            (!scheduledDate && !scheduledDateSelected) ||
            (!isNow && !scheduledDateSelected) ||
            (notificationType === "zone" && !selectedZones.length) ||
            (notificationType === "subZone" && !selectedSubZones.length)
          }
        >
          Enviar Notificación
        </Button>
      </Drawer>
    </Container>
  );
};

export default SendNotification;
